import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, StatCard } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { useAuth } from "@/hooks/useAuth";
import { useKeyboard } from "@/hooks/useKeyboard";
import { useSync } from "@/hooks/useSync";
import { recentLocalIsolates } from "@/lib/db";
import { formatRelative } from "@/lib/format";
import { strings } from "@/lib/strings";
import type { LocalIsolate } from "@/lib/types";

/**
 * Volunteer home (docs/08 §8.2).
 *
 * Fully usable offline: every number here comes from IndexedDB, not the API.
 */
export function VolunteerHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const sync = useSync();
  const [recent, setRecent] = useState<LocalIsolate[]>([]);

  const load = useCallback(async () => {
    setRecent(await recentLocalIsolates(10));
  }, []);

  useEffect(() => {
    void load();
  }, [load, sync.pending, sync.running]);

  // docs/08 §8.2: N starts a new isolate.
  useKeyboard([
    { key: "n", description: "New isolate", handler: () => navigate("/entry") },
  ]);

  const today = new Date().toISOString().slice(0, 10);
  const enteredToday = recent.filter((row) =>
    row.client_created_at.startsWith(today),
  ).length;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">
          {user ? `Hello, ${user.full_name.split(" ")[0]}` : "Hello"}
        </h1>
        <p className="text-sm text-slate-500">
          Press <kbd className="rounded border px-1 font-mono text-xs">N</kbd> to start
          a new isolate.
        </p>
      </div>

      <Button size="lg" className="w-full sm:w-auto" onClick={() => navigate("/entry")}>
        {strings.entry.title}
      </Button>

      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Entered today" value={enteredToday} />
        <StatCard
          label="Waiting to sync"
          value={sync.pending}
          tone={sync.pending > 0 ? "warning" : "ok"}
        />
        <StatCard
          label="Needs attention"
          value={sync.conflicts}
          tone={sync.conflicts > 0 ? "blocking" : "default"}
          hint={sync.conflicts > 0 ? "Open the sync queue" : undefined}
        />
      </div>

      <Card>
        <CardHeader
          title="Your last entries"
          actions={
            <Link to="/isolates" className="text-sm text-brand-700 underline">
              See all
            </Link>
          }
        />
        {recent.length === 0 ? (
          <EmptyState
            title="Nothing entered on this device yet."
            description="Entries you make will appear here, whether or not you are online."
          />
        ) : (
          <CardBody className="divide-y divide-slate-100 p-0">
            {recent.map((row) => (
              <div key={row.client_uuid} className="flex items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-slate-900">
                    {row.record_id ?? "Not yet uploaded"}
                    <span className="ml-2 text-slate-500">
                      {row.payload.lab_number ?? ""}
                    </span>
                  </p>
                  <p className="text-xs text-slate-500">
                    {formatRelative(row.client_created_at)}
                  </p>
                </div>
                <StateBadge state={row.state} />
                {row.payload.status === "draft" && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      navigate(`/entry?edit=${row.server_id || row.client_uuid}`)
                    }
                  >
                    Edit Draft
                  </Button>
                )}
              </div>
            ))}
          </CardBody>
        )}
      </Card>
    </div>
  );
}

function StateBadge({ state }: { state: LocalIsolate["state"] }) {
  switch (state) {
    case "synced":
      return <Badge tone="ok">{strings.sync.syncedBadge}</Badge>;
    case "pending":
      return <Badge tone="warning">{strings.sync.pendingBadge}</Badge>;
    case "syncing":
      return <Badge tone="info">Uploading</Badge>;
    case "conflict":
    case "error":
      return <Badge tone="blocking">{strings.sync.conflictBadge}</Badge>;
    default:
      return <Badge>Draft</Badge>;
  }
}
